import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Download, Play, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Progress } from "../ui/progress";
import { fetchBenchmarkV2Config, runBenchmarkV2 } from "../../lib/doeApi";

const METHOD_COLORS = {
  iterative: "#2563EB",
  linearized: "#16A34A",
  opf: "#F59E0B",
  model_free: "#DC2626",
  linearized_3ph: "#7C3AED",
  traditional_oe: "#DB2777",
  opendss: "#CA8A04",
};

const METHOD_MATRIX = {
  iterative: [1, 3, 1, 1, 3],
  linearized: [3, 2, 1, 1, 2],
  opf: [1, 2, 3, 1, 2],
  model_free: [1, 3, 1, 1, 3],
  linearized_3ph: [3, 2, 1, 3, 2],
  traditional_oe: [2, 1, 3, 3, 2],
  opendss: [1, 3, 1, 2, 3],
};

const MATRIX_DIMS = ["Speed", "Envelope", "Simultaneity", "Phase detail", "Robustness"];
const SCORE_LABELS = { 1: "Low", 2: "Med", 3: "High" };
const LINE_CHART_INITIAL_SIZE = { width: 900, height: 380 };
const TOTALS_CHART_INITIAL_SIZE = { width: 900, height: 420 };
const SOLVE_CHART_INITIAL_SIZE = { width: 900, height: 360 };

function readCssVar(name, fallback) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function useChartColors() {
  const [colors, setColors] = useState(() => ({
    imp: "#002FA7",
    exp: "#0284C7",
    grid: "#e4e4e7",
    axis: "#52525b",
    bg: "#ffffff",
    border: "#e4e4e7",
  }));

  useEffect(() => {
    const update = () =>
      setColors({
        imp: readCssVar("--doe-import", "#002FA7"),
        exp: readCssVar("--doe-export", "#0284C7"),
        grid: readCssVar("--grid-line", "#e4e4e7"),
        axis: readCssVar("--axis-text", "#52525b"),
        bg: `hsl(${readCssVar("--background", "0 0% 100%")})`,
        border: `hsl(${readCssVar("--border", "240 6% 90%")})`,
      });
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  return colors;
}

const fmtKw = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} kW` : "-";
};

const fmtMw = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(Math.abs(n) < 10 ? 2 : 1)} MW` : "-";
};

const fmtSeconds = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${n.toFixed(n < 10 ? 3 : 1)}s`;
};

const fmtPu = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(4)} p.u.` : "-";
};

const fmtPct = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(1)}%` : "-";
};

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function downloadCsv(rows, filename) {
  if (!rows?.length) return;
  const columns = Array.from(rows.reduce((set, row) => {
    Object.keys(row).forEach((key) => set.add(key));
    return set;
  }, new Set()));
  const csv = [
    columns.map(csvEscape).join(","),
    ...rows.map((row) => columns.map((col) => csvEscape(row[col])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function rowLabel(row) {
  return `${row.method} [${row.engine}]`;
}

function groupNetworks(networks) {
  return networks.reduce((groups, network) => {
    const key = network.category || "Networks";
    groups[key] = groups[key] || [];
    groups[key].push(network);
    return groups;
  }, {});
}

function pivotTimeseries(rows, valueKey, selectedMethods) {
  const byStep = new Map();
  const series = [];
  const seen = new Set();
  for (const row of rows || []) {
    if (selectedMethods?.length && !selectedMethods.includes(row.method) && row.method !== "opendss") continue;
    const step = Number(row.time_step);
    if (!Number.isFinite(step)) continue;
    const label = rowLabel(row);
    if (!seen.has(label)) {
      seen.add(label);
      series.push({ key: label, method: row.method, engine: row.engine });
    }
    const current = byStep.get(step) || { time_step: step, hour: `${String(step).padStart(2, "0")}:00` };
    const value = Number(row[valueKey]);
    current[label] = Number.isFinite(value) ? value : null;
    byStep.set(step, current);
  }
  return {
    data: Array.from(byStep.values()).sort((a, b) => a.time_step - b.time_step),
    series,
  };
}

function ChartFrame({ title, children, action }) {
  return (
    <div className="border border-border rounded-[2px] bg-background p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h4 className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground">
          {title}
        </h4>
        {action}
      </div>
      {children}
    </div>
  );
}

function DynamicLineChart({ rows, valueKey, yLabel, selectedMethods, limitLines = [] }) {
  const c = useChartColors();
  const { data, series } = useMemo(
    () => pivotTimeseries(rows, valueKey, selectedMethods),
    [rows, selectedMethods, valueKey],
  );

  if (!data.length) {
    return (
      <div className="h-[320px] grid place-items-center text-xs font-mono text-muted-foreground">
        no time-series rows
      </div>
    );
  }

  return (
    <div className="h-[380px] min-h-[320px] min-w-0 w-full">
      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={LINE_CHART_INITIAL_SIZE}>
        <LineChart data={data} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid stroke={c.grid} strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="hour"
            tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis }}
            tickLine={false}
            axisLine={{ stroke: c.grid }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis }}
            tickLine={false}
            axisLine={{ stroke: c.grid }}
            label={{
              value: yLabel,
              angle: -90,
              position: "insideLeft",
              style: { fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis },
            }}
          />
          <Tooltip
            contentStyle={{
              fontFamily: "IBM Plex Mono",
              fontSize: 11,
              borderRadius: 2,
              border: `1px solid ${c.border}`,
              background: c.bg,
            }}
          />
          <Legend wrapperStyle={{ fontFamily: "IBM Plex Sans", fontSize: 11, color: c.axis }} />
          {limitLines.map((line) => (
            <ReferenceLine
              key={line.value}
              y={line.value}
              stroke={line.color || "#CA8A04"}
              strokeDasharray="4 4"
              label={{ value: line.label, fill: c.axis, fontSize: 10 }}
            />
          ))}
          {series.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              connectNulls
              dot={{ r: 2 }}
              stroke={METHOD_COLORS[s.method] || c.imp}
              strokeWidth={2}
              strokeDasharray={s.engine === "opendss" ? "5 4" : undefined}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function NetTotalsChart({ records }) {
  const c = useChartColors();
  const data = (records || []).map((row) => ({
    ...row,
    label: rowLabel(row),
    net_import_kw: Number(row.net_import_kw) || 0,
    net_export_kw: Number(row.net_export_kw) || 0,
  }));

  return (
    <div className="h-[420px] min-h-[340px] min-w-0 w-full">
      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={TOTALS_CHART_INITIAL_SIZE}>
        <BarChart data={data} margin={{ top: 16, right: 24, left: 8, bottom: 72 }}>
          <CartesianGrid stroke={c.grid} strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="label"
            angle={-28}
            textAnchor="end"
            height={72}
            tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis }}
            tickLine={false}
            axisLine={{ stroke: c.grid }}
          />
          <YAxis tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis }} tickLine={false} axisLine={{ stroke: c.grid }} />
          <Tooltip contentStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, borderRadius: 2, border: `1px solid ${c.border}`, background: c.bg }} formatter={(v) => fmtKw(v)} />
          <Legend wrapperStyle={{ fontFamily: "IBM Plex Sans", fontSize: 11, color: c.axis }} />
          <Bar dataKey="net_import_kw" name="Net import" fill={c.imp} maxBarSize={28} />
          <Bar dataKey="net_export_kw" name="Net export" fill={c.exp} maxBarSize={28} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SolveTimeChart({ records }) {
  const c = useChartColors();
  const data = [...(records || [])]
    .sort((a, b) => (Number(a.solve_time_s) || 0) - (Number(b.solve_time_s) || 0))
    .map((row) => ({ ...row, label: rowLabel(row), solve_time_s: Number(row.solve_time_s) || 0 }));

  return (
    <div className="h-[360px] min-h-[320px] min-w-0 w-full">
      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={SOLVE_CHART_INITIAL_SIZE}>
        <BarChart data={data} layout="vertical" margin={{ top: 16, right: 24, left: 80, bottom: 8 }}>
          <CartesianGrid stroke={c.grid} strokeDasharray="2 4" horizontal={false} />
          <XAxis type="number" tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: c.axis }} tickLine={false} axisLine={{ stroke: c.grid }} />
          <YAxis type="category" dataKey="label" width={150} tick={{ fontFamily: "IBM Plex Sans", fontSize: 10, fill: c.axis }} tickLine={false} axisLine={{ stroke: c.grid }} />
          <Tooltip contentStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, borderRadius: 2, border: `1px solid ${c.border}`, background: c.bg }} formatter={(v) => fmtSeconds(v)} />
          <Bar dataKey="solve_time_s" name="Solve time">
            {data.map((row) => (
              <Cell key={row.label} fill={METHOD_COLORS[row.method] || c.imp} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ResultsTable({ records }) {
  return (
    <div className="border border-border rounded-[2px] overflow-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary/60 hover:bg-secondary/60">
            {["Method", "Engine", "Status", "Solve", "Net import", "Net export", "Avg ms/step", "Load", "Gen", "Participants"].map((h) => (
              <TableHead key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] whitespace-nowrap">
                {h}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {(records || []).map((row) => (
            <TableRow key={`${row.method}-${row.engine}`}>
              <TableCell className="px-4 py-3 font-medium">{row.method}</TableCell>
              <TableCell className="px-4 py-3 text-sm text-muted-foreground">{row.engine}</TableCell>
              <TableCell className="px-4 py-3">
                <span className={
                  String(row.status).toLowerCase().includes("failed")
                    ? "text-destructive font-mono text-xs uppercase"
                    : "text-[var(--doe-export)] font-mono text-xs uppercase"
                }>
                  {row.status}
                </span>
                {row.notes && (
                  <div className="mt-1 max-w-xs truncate text-[11px] text-muted-foreground" title={row.notes}>
                    {row.notes}
                  </div>
                )}
              </TableCell>
              <TableCell className="px-4 py-3 font-mono text-right">{fmtSeconds(row.solve_time_s)}</TableCell>
              <TableCell className="px-4 py-3 font-mono text-right text-[var(--doe-import)]">{fmtKw(row.net_import_kw)}</TableCell>
              <TableCell className="px-4 py-3 font-mono text-right text-[var(--doe-export)]">{fmtKw(row.net_export_kw)}</TableCell>
              <TableCell className="px-4 py-3 font-mono text-right">{Number(row.avg_solve_ms_per_step || 0).toFixed(1)}</TableCell>
              <TableCell className="px-4 py-3 font-mono text-right">{fmtMw(row.total_load_mw)}</TableCell>
              <TableCell className="px-4 py-3 font-mono text-right">{fmtMw(row.total_gen_mw)}</TableCell>
              <TableCell className="px-4 py-3 font-mono text-right">{row.n_participant_buses ?? "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function TimeStepPreview({ rows, filename }) {
  const preview = (rows || []).slice(0, 96);
  const columns = preview.length ? Object.keys(preview[0]).slice(0, 10) : [];
  return (
    <div className="mt-4 border border-border rounded-[2px] overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between gap-4 bg-secondary/40 border-b border-border">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
          Time-step rows
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 rounded-[2px] gap-2"
          disabled={!rows?.length}
          onClick={() => downloadCsv(rows, filename)}
        >
          <Download className="size-3.5" />
          CSV
        </Button>
      </div>
      {!preview.length ? (
        <div className="p-6 text-xs font-mono text-muted-foreground">no rows</div>
      ) : (
        <div className="max-h-[300px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-background hover:bg-background">
                {columns.map((col) => (
                  <TableHead key={col} className="px-3 py-2 text-[10px] uppercase tracking-[0.14em] whitespace-nowrap">
                    {col}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.map((row, idx) => (
                <TableRow key={idx}>
                  {columns.map((col) => (
                    <TableCell key={col} className="px-3 py-2 text-xs font-mono whitespace-nowrap">
                      {row[col] ?? "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function MethodMatrix({ methods }) {
  const visible = [...methods, "opendss"].filter((method, idx, arr) => METHOD_MATRIX[method] && arr.indexOf(method) === idx);
  return (
    <div className="border border-border rounded-[2px] overflow-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary/60 hover:bg-secondary/60">
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em]">Method</TableHead>
            {MATRIX_DIMS.map((dim) => (
              <TableHead key={dim} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-center">
                {dim}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.map((method) => (
            <TableRow key={method}>
              <TableCell className="px-4 py-3 font-mono">{method}</TableCell>
              {METHOD_MATRIX[method].map((score, idx) => (
                <TableCell key={`${method}-${idx}`} className="px-4 py-3 text-center">
                  <span className={`inline-flex min-w-14 justify-center rounded-[2px] border px-2 py-1 text-xs font-mono ${
                    score === 3
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : score === 2
                        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                        : "border-muted-foreground/30 bg-secondary text-muted-foreground"
                  }`}>
                    {SCORE_LABELS[score]}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function BenchmarkV2Panel() {
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    network_name: "case14",
    n_steps: 24,
    methods: ["iterative", "linearized", "opf", "model_free", "linearized_3ph", "traditional_oe"],
    load_profile_name: "residential_daily",
    v_min: 0.95,
    v_max: 1.05,
    probe_ceiling_mw: 5,
    thermal_limit_pct: 100,
    include_opendss: true,
  });

  useEffect(() => {
    fetchBenchmarkV2Config()
      .then((data) => {
        setConfig(data);
        if (data?.defaults) {
          setForm((current) => ({ ...current, ...data.defaults }));
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Unable to load Benchmark v2 metadata");
      })
      .finally(() => setLoadingConfig(false));
  }, []);

  const networkGroups = useMemo(() => groupNetworks(config?.networks || []), [config]);
  const records = useMemo(() => result?.records || [], [result]);
  const timeseries = useMemo(() => result?.timeseries || [], [result]);
  const selectedMethods = form.methods;
  const activeTimeseries = useMemo(
    () => timeseries.filter((row) => selectedMethods.includes(row.method) || row.method === "opendss"),
    [timeseries, selectedMethods],
  );

  const metrics = useMemo(() => {
    const ppRows = records.filter((row) => row.engine === "pandapower");
    const bestExport = ppRows.reduce((best, row) => (
      Number(row.net_export_kw) > Number(best?.net_export_kw ?? -Infinity) ? row : best
    ), null);
    const bestImport = ppRows.reduce((best, row) => (
      Number(row.net_import_kw) > Number(best?.net_import_kw ?? -Infinity) ? row : best
    ), null);
    const fastest = ppRows.reduce((best, row) => (
      Number(row.solve_time_s) < Number(best?.solve_time_s ?? Infinity) ? row : best
    ), null);
    const nums = (key) => activeTimeseries.map((row) => Number(row[key])).filter(Number.isFinite);
    const vmins = nums("v_min_pu");
    const vmaxs = nums("v_max_pu");
    const lines = nums("max_line_loading_pct");
    const trafos = nums("max_trafo_loading_pct");
    return {
      bestExport,
      bestImport,
      fastest,
      worstVmin: vmins.length ? Math.min(...vmins) : null,
      worstVmax: vmaxs.length ? Math.max(...vmaxs) : null,
      worstLine: lines.length ? Math.max(...lines) : null,
      worstTrafo: trafos.length ? Math.max(...trafos) : null,
      successCount: records.filter((row) => !String(row.status).toLowerCase().includes("failed")).length,
    };
  }, [records, activeTimeseries]);

  const setNumber = (key, value) => {
    const parsed = Number(value);
    setForm((current) => ({ ...current, [key]: Number.isFinite(parsed) ? parsed : current[key] }));
  };

  const toggleMethod = (methodId, checked) => {
    setForm((current) => {
      const next = checked
        ? Array.from(new Set([...current.methods, methodId]))
        : current.methods.filter((id) => id !== methodId);
      return { ...current, methods: next };
    });
  };

  const handleRun = async () => {
    if (!form.methods.length) {
      toast.error("Select at least one Benchmark v2 method");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const data = await runBenchmarkV2({
        ...form,
        n_steps: Math.max(1, Math.min(168, Math.trunc(Number(form.n_steps) || 24))),
      });
      setResult(data);
      const failed = (data.records || []).filter((row) => String(row.status).toLowerCase().includes("failed")).length;
      if (failed) {
        toast.error(`Benchmark v2 completed with ${failed} failed run${failed === 1 ? "" : "s"}`);
      } else {
        toast.success(`Benchmark v2 returned ${data.counts?.records ?? 0} run records`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Benchmark v2 failed");
    } finally {
      setRunning(false);
    }
  };

  if (loadingConfig) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="border border-dashed border-border rounded-[2px] p-12 text-center bg-secondary/30 text-xs font-mono text-muted-foreground">
          loading Benchmark v2 metadata...
        </div>
      </main>
    );
  }

  return (
    <>
      <section className="border-b border-border" data-testid="benchmark-v2-control-panel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
            <div className="lg:col-span-3">
              <Label htmlFor="benchmark-v2-network" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Network
              </Label>
              <Select value={form.network_name} onValueChange={(v) => setForm((current) => ({ ...current, network_name: v }))}>
                <SelectTrigger id="benchmark-v2-network" className="mt-2 h-11 rounded-[2px] font-mono text-sm" data-testid="benchmark-v2-network-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-[2px]">
                  {Object.entries(networkGroups).map(([category, list]) => (
                    <SelectGroup key={category}>
                      <SelectLabel className="text-[10px] uppercase tracking-[0.18em]">{category}</SelectLabel>
                      {list.map((network) => (
                        <SelectItem key={network.id} value={network.id} className="font-mono text-sm">
                          {network.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="lg:col-span-2">
              <Label htmlFor="benchmark-v2-profile" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Load profile
              </Label>
              <Select value={form.load_profile_name} onValueChange={(v) => setForm((current) => ({ ...current, load_profile_name: v }))}>
                <SelectTrigger id="benchmark-v2-profile" className="mt-2 h-11 rounded-[2px] font-mono text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-[2px]">
                  {(config?.profiles || []).map((profile) => (
                    <SelectItem key={profile.id} value={profile.id} className="font-mono text-sm">
                      {profile.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="benchmark-v2-steps" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Steps
              </Label>
              <Input id="benchmark-v2-steps" type="number" min={1} max={168} value={form.n_steps} onChange={(e) => setNumber("n_steps", e.target.value)} className="mt-2 h-11 rounded-[2px] font-mono" />
            </div>

            <div>
              <Label htmlFor="benchmark-v2-vmin" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Vmin
              </Label>
              <Input id="benchmark-v2-vmin" type="number" step="0.01" value={form.v_min} onChange={(e) => setNumber("v_min", e.target.value)} className="mt-2 h-11 rounded-[2px] font-mono" />
            </div>

            <div>
              <Label htmlFor="benchmark-v2-vmax" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Vmax
              </Label>
              <Input id="benchmark-v2-vmax" type="number" step="0.01" value={form.v_max} onChange={(e) => setNumber("v_max", e.target.value)} className="mt-2 h-11 rounded-[2px] font-mono" />
            </div>

            <div>
              <Label htmlFor="benchmark-v2-probe" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Probe MW
              </Label>
              <Input id="benchmark-v2-probe" type="number" step="0.5" value={form.probe_ceiling_mw} onChange={(e) => setNumber("probe_ceiling_mw", e.target.value)} className="mt-2 h-11 rounded-[2px] font-mono" />
            </div>

            <div>
              <Label htmlFor="benchmark-v2-thermal" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Thermal %
              </Label>
              <Input id="benchmark-v2-thermal" type="number" step="5" value={form.thermal_limit_pct} onChange={(e) => setNumber("thermal_limit_pct", e.target.value)} className="mt-2 h-11 rounded-[2px] font-mono" />
            </div>

            <div className="lg:col-span-2 flex">
              <Button
                type="button"
                onClick={handleRun}
                disabled={running || !form.network_name}
                className="w-full h-11 rounded-[2px] bg-primary text-primary-foreground hover:bg-primary/90 font-semibold tracking-wide uppercase text-xs gap-2"
                data-testid="run-benchmark-v2-button"
              >
                {running ? <RefreshCw className="size-4 animate-spin" /> : <Play className="size-4" />}
                {running ? "Running" : "Run v2"}
              </Button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-10 border border-border rounded-[2px] p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-3">
                Methods
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(config?.methods || []).map((method) => (
                  <label key={method.id} className="flex items-start gap-3 border border-border rounded-[2px] p-3 bg-background">
                    <Checkbox
                      checked={form.methods.includes(method.id)}
                      onCheckedChange={(checked) => toggleMethod(method.id, checked === true)}
                      className="mt-0.5 rounded-[2px]"
                    />
                    <span>
                      <span className="block text-sm font-medium">{method.label}</span>
                      <span className="block text-[11px] text-muted-foreground leading-snug">{method.sub}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <label className="lg:col-span-2 flex items-center gap-3 border border-border rounded-[2px] p-4 bg-background">
              <Checkbox
                checked={form.include_opendss}
                onCheckedChange={(checked) => setForm((current) => ({ ...current, include_opendss: checked === true }))}
                className="rounded-[2px]"
              />
              <span>
                <span className="block text-sm font-medium">OpenDSS</span>
                <span className="block text-[11px] text-muted-foreground">MV cross-validation</span>
              </span>
            </label>
          </div>
        </div>

        {running && (
          <>
            <div className="relative h-[2px] w-full overflow-hidden bg-secondary scan-line" />
            <div className="border-t border-border bg-secondary/30">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2">
                  <span>Benchmark v2 solver active</span>
                  <span className="font-mono text-foreground">{form.methods.length + (form.include_opendss ? 1 : 0)} run units</span>
                </div>
                <Progress indeterminate className="h-1.5 rounded-[2px] bg-secondary" />
              </div>
            </div>
          </>
        )}
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="benchmark-v2-section">
        {!result && !running && (
          <div className="border border-dashed border-border rounded-[2px] p-12 text-center bg-secondary/30">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
              benchmark v2.0
            </div>
            <h3 className="mt-3 text-xl font-semibold tracking-tight">
              Configure a study and run the accurate DOE benchmark path
            </h3>
          </div>
        )}

        {result && (
          <>
            <div className="mb-8 grid grid-cols-2 lg:grid-cols-6 gap-px bg-border border border-border rounded-[2px] overflow-hidden">
              <MetricCell label="Highest export" value={fmtKw(metrics.bestExport?.net_export_kw)} note={metrics.bestExport?.method} />
              <MetricCell label="Highest import" value={fmtKw(metrics.bestImport?.net_import_kw)} note={metrics.bestImport?.method} />
              <MetricCell label="Fastest" value={fmtSeconds(metrics.fastest?.solve_time_s)} note={metrics.fastest?.method} />
              <MetricCell label="Voltage extrema" value={`${fmtPu(metrics.worstVmin)} / ${fmtPu(metrics.worstVmax)}`} />
              <MetricCell label="Thermal extrema" value={`${fmtPct(metrics.worstLine)} / ${fmtPct(metrics.worstTrafo)}`} />
              <MetricCell label="Succeeded" value={`${metrics.successCount} / ${records.length}`} note="method x engine" />
            </div>

            <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                  Benchmark v2.0 results
                </div>
                <h3 className="text-2xl tracking-tight font-semibold mt-1">
                  {result.config?.network_name}
                </h3>
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  profile={result.config?.load_profile_name} · steps={result.config?.n_steps} · V=[{result.config?.v_min}, {result.config?.v_max}] p.u. · rows={result.counts?.timeseries}
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="h-9 rounded-[2px] gap-2" onClick={() => downloadCsv(records, "doe_benchmark_v2_records.csv")}>
                  <Download className="size-3.5" />
                  Records
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-9 rounded-[2px] gap-2" onClick={() => downloadCsv(timeseries, "doe_benchmark_v2_timeseries.csv")}>
                  <Download className="size-3.5" />
                  Time series
                </Button>
              </div>
            </div>

            <Tabs defaultValue="export" className="w-full">
              <TabsList className="w-full justify-start overflow-x-auto rounded-none border border-border bg-background p-0">
                {[
                  ["export", "Export DOE"],
                  ["import", "Import DOE"],
                  ["net", "Net totals"],
                  ["voltage", "Voltage"],
                  ["thermal", "Thermal"],
                  ["solve", "Solve time"],
                  ["matrix", "Method matrix"],
                  ["table", "Table"],
                ].map(([value, label]) => (
                  <TabsTrigger key={value} value={value} className="h-10 rounded-none border-r border-border px-5 data-[state=active]:bg-secondary">
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="export" className="mt-6">
                <ChartFrame title="Export DOE time series">
                  <DynamicLineChart rows={activeTimeseries} valueKey="total_export_kw" yLabel="kW" selectedMethods={selectedMethods} />
                  <TimeStepPreview rows={activeTimeseries} filename="doe_benchmark_v2_export_timeseries.csv" />
                </ChartFrame>
              </TabsContent>

              <TabsContent value="import" className="mt-6">
                <ChartFrame title="Import DOE time series">
                  <DynamicLineChart rows={activeTimeseries} valueKey="total_import_kw" yLabel="kW" selectedMethods={selectedMethods} />
                  <TimeStepPreview rows={activeTimeseries} filename="doe_benchmark_v2_import_timeseries.csv" />
                </ChartFrame>
              </TabsContent>

              <TabsContent value="net" className="mt-6">
                <ChartFrame title="Net import and export totals">
                  <NetTotalsChart records={records} />
                  <TimeStepPreview rows={activeTimeseries.map((row) => ({
                    ...row,
                    net_export_minus_import_kw: Number(row.total_export_kw || 0) - Number(row.total_import_kw || 0),
                  }))} filename="doe_benchmark_v2_net_timeseries.csv" />
                </ChartFrame>
              </TabsContent>

              <TabsContent value="voltage" className="mt-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <ChartFrame title="Vmax by method and engine">
                    <DynamicLineChart rows={activeTimeseries} valueKey="v_max_pu" yLabel="p.u." selectedMethods={selectedMethods} limitLines={[{ value: Number(result.config?.v_max), label: "Vmax" }]} />
                  </ChartFrame>
                  <ChartFrame title="Vmin by method and engine">
                    <DynamicLineChart rows={activeTimeseries} valueKey="v_min_pu" yLabel="p.u." selectedMethods={selectedMethods} limitLines={[{ value: Number(result.config?.v_min), label: "Vmin" }]} />
                  </ChartFrame>
                </div>
                <TimeStepPreview rows={activeTimeseries} filename="doe_benchmark_v2_voltage_timeseries.csv" />
              </TabsContent>

              <TabsContent value="thermal" className="mt-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <ChartFrame title="Line loading">
                    <DynamicLineChart rows={activeTimeseries} valueKey="max_line_loading_pct" yLabel="%" selectedMethods={selectedMethods} limitLines={[{ value: Number(result.config?.thermal_limit_pct), label: "limit" }]} />
                  </ChartFrame>
                  <ChartFrame title="Transformer loading">
                    <DynamicLineChart rows={activeTimeseries} valueKey="max_trafo_loading_pct" yLabel="%" selectedMethods={selectedMethods} limitLines={[{ value: Number(result.config?.thermal_limit_pct), label: "limit" }]} />
                  </ChartFrame>
                </div>
                <TimeStepPreview rows={activeTimeseries} filename="doe_benchmark_v2_thermal_timeseries.csv" />
              </TabsContent>

              <TabsContent value="solve" className="mt-6">
                <ChartFrame title="Solve time by method and engine">
                  <SolveTimeChart records={records} />
                  <TimeStepPreview rows={activeTimeseries} filename="doe_benchmark_v2_solve_timeseries.csv" />
                </ChartFrame>
              </TabsContent>

              <TabsContent value="matrix" className="mt-6">
                <ChartFrame title="Method characterization matrix">
                  <MethodMatrix methods={selectedMethods} />
                  <TimeStepPreview rows={activeTimeseries} filename="doe_benchmark_v2_method_matrix_timeseries.csv" />
                </ChartFrame>
              </TabsContent>

              <TabsContent value="table" className="mt-6">
                <ChartFrame title="Benchmark records">
                  <ResultsTable records={records} />
                </ChartFrame>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </>
  );
}

function MetricCell({ label, value, note }) {
  return (
    <div className="bg-background px-5 py-5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
        {label}
      </div>
      <div className="mt-2 text-xl font-mono font-semibold tracking-tight break-words">
        {value || "-"}
      </div>
      {note && <div className="mt-1 text-[11px] text-muted-foreground">{note}</div>}
    </div>
  );
}
